/**
 * Mapa de los veinte dientes de leche: cuáles se han caído ya y qué toca ahora.
 *
 * La guía `cuando-se-caen-los-dientes-de-leche` publicaba una tabla y nada más,
 * así que la intención quedaba resuelta de una lectura. Esto la convierte en un
 * activo de RETORNO: veinte dientes en unos seis años, así que un padre que
 * guarde su mapa vuelve muchas veces. Es la pieza de más recorrido del grupo.
 *
 * Las edades salen de la tabla que la propia guía ya publica — no de otra
 * fuente — para que herramienta y texto no se contradigan.
 */

/** Grupo de dientes que comparten horquilla de caída, tal como los agrupa la guía. */
export type ToothGroupKey =
  | 'lower-central-incisors'
  | 'upper-central-incisors'
  | 'lateral-incisors'
  | 'first-molars'
  | 'canines'
  | 'second-molars'

export type ToothGroup = {
  key: ToothGroupKey
  label: string
  /** Cuántos dientes tiene el grupo (siempre par: mitad por lado). */
  count: number
  /** Horquilla orientativa de caída, en años. */
  from: number
  to: number
}

/**
 * Orden = orden típico de caída, que es lo que la guía subraya que importa más
 * que la edad exacta. Los veinte dientes de leche, agrupados.
 */
export const TOOTH_GROUPS: ToothGroup[] = [
  { key: 'lower-central-incisors', label: 'Incisivos centrales inferiores', count: 2, from: 5, to: 7 },
  { key: 'upper-central-incisors', label: 'Incisivos centrales superiores', count: 2, from: 6, to: 7 },
  { key: 'lateral-incisors', label: 'Incisivos laterales', count: 4, from: 7, to: 8 },
  { key: 'first-molars', label: 'Primeros molares', count: 4, from: 9, to: 11 },
  { key: 'canines', label: 'Caninos (colmillos)', count: 4, from: 9, to: 12 },
  { key: 'second-molars', label: 'Segundos molares', count: 4, from: 10, to: 12 },
]

export const TOTAL_BABY_TEETH = TOOTH_GROUPS.reduce((total, group) => total + group.count, 0)

/** Identificador estable de un diente: `<grupo>:<índice dentro del grupo>`. */
export type ToothId = string

export function toothIdsFor(group: ToothGroup): ToothId[] {
  return Array.from({ length: group.count }, (_, index) => `${group.key}:${index}`)
}

export const ALL_TOOTH_IDS: ToothId[] = TOOTH_GROUPS.flatMap(toothIdsFor)

export function formatAgeRange(group: ToothGroup): string {
  return group.from === group.to ? `${group.from} años` : `${group.from}-${group.to} años`
}

export type ChartProgress = {
  fallen: number
  total: number
  /** Grupos donde aún queda algún diente, en orden típico de caída. */
  pending: ToothGroup[]
  /** Siguiente grupo esperable, o `null` si ya han caído los veinte. */
  next: ToothGroup | null
  complete: boolean
}

/**
 * Estado del mapa. `next` es el primer grupo con dientes pendientes siguiendo el
 * orden típico — no el más cercano en edad: la guía insiste en que el orden es
 * más informativo que la edad exacta, y un padre quiere saber "¿qué toca ahora?".
 */
export function chartProgress(fallenIds: Iterable<ToothId>): ChartProgress {
  const fallenSet = new Set(fallenIds)
  // Ignora ids desconocidos (p.ej. un localStorage de una versión anterior con
  // otros grupos): si no, el contador podría pasarse de veinte.
  const fallen = ALL_TOOTH_IDS.filter((id) => fallenSet.has(id)).length
  const pending = TOOTH_GROUPS.filter((group) =>
    toothIdsFor(group).some((id) => !fallenSet.has(id))
  )

  return {
    fallen,
    total: TOTAL_BABY_TEETH,
    pending,
    next: pending[0] ?? null,
    complete: fallen === TOTAL_BABY_TEETH,
  }
}

/** Clave de `localStorage`. Solo ids de dientes: ningún dato del niño. */
export const TOOTH_CHART_STORAGE_KEY = 'papanoelchat.tooth-chart.v1'

/**
 * Lee el mapa guardado. Tolera basura (JSON roto, forma inesperada, ids de otra
 * versión) devolviendo lista vacía: perder el mapa es molesto, pero romper la
 * guía entera por un localStorage corrupto es peor.
 */
export function parseStoredChart(raw: string | null): ToothId[] {
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const known = new Set(ALL_TOOTH_IDS)
    return parsed.filter((id): id is ToothId => typeof id === 'string' && known.has(id))
  } catch {
    return []
  }
}

export function serializeChart(fallenIds: Iterable<ToothId>): string {
  const fallenSet = new Set(fallenIds)
  // Se guarda en el orden canónico, no en el de click: hace el valor estable y
  // comparable entre sesiones.
  return JSON.stringify(ALL_TOOTH_IDS.filter((id) => fallenSet.has(id)))
}
